import React, { useState, useEffect } from "react";
import { TextField, Button, Grid, Box, Typography, Divider } from "@mui/material";
import { PMDialog } from "../../../utils/PMDialog";
import { useTranslation } from "react-i18next";
import { billingPlanItemsApi } from "../../../../api/billing_plan_items";
import { axiosWithToast } from "../../../Utils";

function parseCommaNumber(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const n = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Forslag til beløp — basert kun på utført for denne perioden/raden og innestående-innstilling.
 * Tidligere faktureringer trekkes IKKE inn (kun info i oversikt).
 */
function retentionAndInvoiceTotals(project, contractSum, executedThisPeriod) {
  const executed =
    typeof executedThisPeriod === "number"
      ? executedThisPeriod
      : parseCommaNumber(executedThisPeriod);

  let retention = 0;
  if (project?.remaining_amount_enabled) {
    const base =
      project.remaining_amount_base === "contract" ? contractSum || 0 : executed;
    const pct = project.remaining_amount_percentage || 10;
    retention = ((base || 0) * pct) / 100;
  }

  const invoiceAmount = project?.remaining_amount_enabled
    ? Math.max(0, executed - retention)
    : Math.max(0, executed);

  return { retention, invoiceAmount, executed };
}

export const BillingPlanItemDialog = ({
  open,
  onClose,
  onSuccess,
  project,
  billingPlanItem,
  contractSum = 0,
  billedSum = 0,
  contractLinesCompletedHint = 0,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    invoice_type: "a_account",
    description: "",
    amount: "",
    planned_date: "",
    status: "planned",
    completed_amount: "",
  });

  const { retention: retentionDisplay } = retentionAndInvoiceTotals(
    project,
    contractSum,
    parseCommaNumber(formData.completed_amount)
  );

  const calculateSuggestedAmount = () => {
    const { invoiceAmount } = retentionAndInvoiceTotals(
      project,
      contractSum,
      parseCommaNumber(formData.completed_amount)
    );
    return invoiceAmount;
  };

  useEffect(() => {
    if (!open) return;

    if (billingPlanItem) {
      const itemCompletedAmount = Number(billingPlanItem.completed_amount) || 0;
      const { invoiceAmount } = retentionAndInvoiceTotals(
        project,
        contractSum,
        itemCompletedAmount
      );
      setFormData({
        invoice_type: billingPlanItem.invoice_type || "a_account",
        description: billingPlanItem.description || "",
        amount: invoiceAmount > 0 ? invoiceAmount.toFixed(2).replace(".", ",") : "",
        planned_date: billingPlanItem.planned_date
          ? new Date(billingPlanItem.planned_date).toISOString().split("T")[0]
          : "",
        status: billingPlanItem.status || "planned",
        completed_amount:
          itemCompletedAmount > 0 ? String(itemCompletedAmount).replace(".", ",") : "",
      });
    } else {
      setFormData({
        invoice_type: "a_account",
        description: "",
        amount: "",
        planned_date: "",
        status: "planned",
        completed_amount: "",
      });
    }
  }, [open, billingPlanItem, project, contractSum]);

  // Forslag til faktureringsbeløp når utført for perioden endres (ikke påvirket av tidligere fakturaer)
  useEffect(() => {
    if (!open) return;

    const completed = parseCommaNumber(formData.completed_amount);
    const { invoiceAmount } = retentionAndInvoiceTotals(project, contractSum, completed);
    const amtStr = invoiceAmount > 0 ? invoiceAmount.toFixed(2).replace(".", ",") : "";
    setFormData((prev) => ({ ...prev, amount: amtStr }));
  }, [
    formData.completed_amount,
    project?.remaining_amount_enabled,
    project?.remaining_amount_base,
    project?.remaining_amount_percentage,
    contractSum,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project?.id) return;

    setLoading(true);

    const payload = {
      invoice_type: formData.invoice_type,
      description: formData.description,
      amount: parseFloat(formData.amount.replace(',', '.')) || 0,
      planned_date: formData.planned_date || null,
      status: formData.status,
      completed_amount: formData.completed_amount ? parseFloat(formData.completed_amount.replace(',', '.')) : null,
    };

    try {
      if (billingPlanItem) {
        await axiosWithToast(
          billingPlanItemsApi.updateBillingPlanItem(project.id, billingPlanItem.id, payload),
          () => {
            onSuccess();
          },
          () => {},
          () => {},
          t("project.billing_plan.updating")
        );
      } else {
        await axiosWithToast(
          billingPlanItemsApi.createBillingPlanItem(project.id, payload),
          () => {
            onSuccess();
          },
          () => {},
          () => {},
          t("project.billing_plan.creating")
        );
      }
    } catch (error) {
      console.error("Error saving billing plan item:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPriceInput = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";
    
    // Convert to number if it's a string
    const numValue = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
    if (isNaN(numValue)) return "";
    
    // Format with Norwegian locale (space as thousand separator, comma as decimal)
    const formatted = new Intl.NumberFormat("no-NO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
    
    return formatted.replace(/\s/g, " ").replace(".", ",");
  };

  const formatPriceWithTwoDecimals = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const stringValue = String(value).trim();
    if (stringValue === "") return "";
    
    if (stringValue.includes(",")) {
      const parts = stringValue.split(",");
      const integerPart = parts[0] || "";
      const decimalPart = parts[1] || "";
      
      let formattedDecimal = decimalPart.substring(0, 2);
      if (formattedDecimal.length === 1) {
        formattedDecimal = formattedDecimal + "0";
      } else if (formattedDecimal.length === 0) {
        formattedDecimal = "00";
      }
      
      return `${integerPart},${formattedDecimal}`;
    }
    
    if (/^\d+$/.test(stringValue)) {
      return `${stringValue},00`;
    }
    
    return stringValue;
  };

  const handlePriceBlur = (e) => {
    const value = e.target.value;
    if (value && value.trim() !== "") {
      const formatted = formatPriceWithTwoDecimals(value);
      setFormData({
        ...formData,
        amount: formatted,
      });
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={billingPlanItem 
        ? t("project.billing_plan.edit_item") 
        : t("project.billing_plan.create_invoice")}
      size="2xl"
      closeOnOutsideClick={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Amount Summary - Only show when creating new item */}
        {!billingPlanItem && (
          <>
            <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("project.billing_plan.summary_title")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("project.billing_plan.contract_sum")}:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Intl.NumberFormat("no-NO", {
                      style: "currency",
                      currency: "NOK",
                    }).format(contractSum || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("project.billing_plan.summary_contract_lines_completed")}:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Intl.NumberFormat("no-NO", {
                      style: "currency",
                      currency: "NOK",
                    }).format(contractLinesCompletedHint || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("project.billing_plan.summary_billed_to_date")}:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ color: "success.main" }}>
                    {new Intl.NumberFormat("no-NO", {
                      style: "currency",
                      currency: "NOK",
                    }).format(billedSum || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("project.billing_plan.summary_retention_for_period")}:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ color: retentionDisplay > 0 ? "warning.main" : "text.secondary" }}>
                    {new Intl.NumberFormat("no-NO", {
                      style: "currency",
                      currency: "NOK",
                    }).format(retentionDisplay || 0)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Divider />
          </>
        )}

        {/* Type */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              select
              label={t("project.billing_plan.type")}
              value={formData.invoice_type}
              onChange={(e) => setFormData({ ...formData, invoice_type: e.target.value })}
              fullWidth
              variant="standard"
              SelectProps={{
                native: true,
              }}
            >
              <option value="a_account">{t("project.order_line.a_account")}</option>
              <option value="final">{t("project.billing_plan.final_invoice")}</option>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("project.billing_plan.planned_date")}
              type="date"
              value={formData.planned_date}
              onChange={(e) => setFormData({ ...formData, planned_date: e.target.value })}
              fullWidth
              variant="standard"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        {/* Beskrivelse */}
        <TextField
          label={t("project.order_line.description")}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          fullWidth
          multiline
          rows={3}
          variant="standard"
          required
        />

        {/* Amount Fields */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("project.billing_plan.completed_amount")}
              value={formData.completed_amount || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Allow empty input
                if (inputValue === "") {
                  setFormData(prev => ({ ...prev, completed_amount: "" }));
                  return;
                }
                
                // Remove all non-numeric characters except comma
                const cleaned = inputValue.replace(/[^0-9,]/g, "");
                // Update formData - this will trigger useEffect to recalculate remaining and invoice amounts
                setFormData(prev => ({ ...prev, completed_amount: cleaned }));
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value && value.trim() !== "") {
                  const formatted = formatPriceWithTwoDecimals(value);
                  setFormData(prev => ({ ...prev, completed_amount: formatted }));
                } else {
                  setFormData(prev => ({ ...prev, completed_amount: "" }));
                }
              }}
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: <span className="ml-2">kr</span>,
              }}
              helperText={t("project.billing_plan.completed_helper", {
                suggested: new Intl.NumberFormat("no-NO", {
                  style: "currency",
                  currency: "NOK",
                }).format(contractLinesCompletedHint || 0),
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("project.billing_plan.remaining_amount")}
              value={formatPriceInput(retentionDisplay) || "0"}
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: <span className="ml-2">kr</span>,
              }}
              disabled
              sx={{
                "& .MuiInputBase-input": {
                  fontWeight: "bold",
                  color: retentionDisplay > 0 ? "warning.main" : "text.primary",
                },
              }}
              helperText={
                project?.remaining_amount_enabled
                  ? t("project.billing_plan.retention_helper_enabled", {
                      pct: project.remaining_amount_percentage || 10,
                      base:
                        project.remaining_amount_base === "contract"
                          ? t("project.billing_plan.retention_base_contract")
                          : t("project.billing_plan.retention_base_completed"),
                    })
                  : t("project.billing_plan.retention_helper_disabled")
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("project.billing_plan.amount_to_invoice")}
              value={formData.amount || ""}
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: <span className="ml-2">kr</span>,
              }}
              disabled
              required
              sx={{
                "& .MuiInputBase-input": {
                  fontWeight: "medium",
                },
              }}
              helperText={t("project.billing_plan.amount_to_invoice_hint")}
            />
          </Grid>
        </Grid>
        
        {!billingPlanItem && project?.remaining_amount_enabled && (
          <p className="text-xs text-gray-500 mt-1">
            {t("project.billing_plan.footer_suggested_line", {
              amount: new Intl.NumberFormat("no-NO", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(calculateSuggestedAmount()),
              pct: project.remaining_amount_percentage || 10,
              base:
                project.remaining_amount_base === "contract"
                  ? t("project.billing_plan.retention_base_contract")
                  : t("project.billing_plan.retention_base_completed"),
            })}
          </p>
        )}

        {/* Status (kun ved redigering) */}
        {billingPlanItem && (
          <TextField
            select
            label={t("project.billing_plan.status.title")}
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            fullWidth
            variant="standard"
            SelectProps={{
              native: true,
            }}
          >
            <option value="planned">{t("project.billing_plan.status.planned")}</option>
            <option value="billed">{t("project.billing_plan.status.billed")}</option>
            <option value="cancelled">{t("project.billing_plan.status.cancelled")}</option>
          </TextField>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
          <Button onClick={onClose} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading 
              ? t("common.saving") 
              : billingPlanItem 
                ? t("common.update") 
                : t("common.create")}
          </Button>
        </div>
      </form>
    </PMDialog>
  );
};

